/**
 * Supabase Auth Composable
 *
 * Provides reactive authentication state and methods for Vue components.
 *
 * IMPORTANT: This composable shares state with useAuth.ts to ensure
 * router guards and components see the same auth state.
 */

import { computed } from 'vue'
import { supabase } from '../config/supabase'
import { authSession, authUser, authLoading, waitForAuthInit } from './useAuth'

// Re-export for compatibility
export { waitForAuthInit }

// Use shared state from useAuth.ts
const session = authSession
const user = authUser
const loading = authLoading

/**
 * Main auth composable - use in any component
 */
export function useSupabaseAuth() {
  const isSignedIn = computed(() => !!session.value)
  const userEmail = computed(() => user.value?.email ?? null)
  const userId = computed(() => user.value?.id ?? null)

  // Auth methods
  async function signInWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    })
    if (error) {
      console.error('[Auth] Google sign in error:', error)
      throw error
    }
  }

  async function signInWithApple(): Promise<void> {
    console.log('[Auth] Calling supabase.auth.signInWithOAuth for Apple...')
    console.log('[Auth] Redirect URL:', `${window.location.origin}/dashboard`)

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    })

    console.log('[Auth] signInWithOAuth response:', { data, error })

    if (error) {
      console.error('[Auth] Apple sign in error:', error)
      throw error
    }
  }

  async function signInWithEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    })
    if (error) {
      console.error('[Auth] Email sign in error:', error)
      throw error
    }
  }

  async function signOut(): Promise<void> {
    const { data: { session: currentSession } } = await supabase.auth.getSession()
    if (!currentSession) {
      user.value = null
      session.value = null
      return
    }

    const { error } = await supabase.auth.signOut()
    if (error) {
      if (error.name === 'AuthSessionMissingError') {
        user.value = null
        session.value = null
        return
      }
      console.error('[Auth] Sign out error:', error)
      throw error
    }
  }

  /**
   * Get current access token for API calls
   */
  async function getAccessToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  }

  return {
    // State
    user,
    session,
    loading,
    isSignedIn,
    userEmail,
    userId,

    // Methods
    signInWithGoogle,
    signInWithApple,
    signInWithEmail,
    signOut,
    getAccessToken,
  }
}
